import { jsx } from "react/jsx-runtime";
import { Alert } from "@zillow/constellation";
import { dynamic } from "@zillow/frontier/dynamic";
import { remote } from "@zillow/frontier/import";
import { useBlockEnvironment } from "@zillow/zrs-cms";
const frontierBlockConfigs = /* @__PURE__ */ new Map();
let frontierBlocks = {};
function setFrontierBlocksRegistry(registry) {
  frontierBlocks = registry;
}
function createFrontierBlock(blockName, remoteModule) {
  if (!blockName.includes("/")) {
    throw new Error('Block name must include a namespace (e.g., "cms-frontier/blockname" or "custom-namespace/block-name")');
  }
  frontierBlockConfigs.set(blockName, {
    blockName,
    remoteModule
  });
  let blockMetadata = null;
  let RemoteComponent = null;
  const createDynamicComponent = () => {
    {
      return dynamic(() => remote(remoteModule), {
        render: "ssr",
        type: "remote",
        module: remoteModule,
        webpack: () => [require.resolve("@zillow/frontier/dynamic"), remoteModule]
      });
    }
  };
  const BlockComponent = ({
    attrs,
    ...props
  }) => {
    const {
      environment
    } = useBlockEnvironment();
    if (environment === "editor") {
      return /* @__PURE__ */ jsx(Alert, { body: (blockMetadata == null ? void 0 : blockMetadata.editorPlaceholder) || `Editor placeholder for ${blockName}` });
    }
    if (!RemoteComponent) {
      return null;
    }
    return /* @__PURE__ */ jsx(RemoteComponent, { ...attrs, ...props });
  };
  const blockExport = {
    BLOCK_NAME: blockName,
    metadata: blockMetadata,
    register(registry) {
      registry[blockName] = {
        Component: BlockComponent,
        metadata: blockMetadata,
        updateMetadata: (newMetadata) => {
          blockMetadata = {
            ...newMetadata,
            name: blockName,
            // Always preserve block name
            remoteModule
            // Always preserve remote module
          };
          if (registry[blockName]) {
            registry[blockName].metadata = blockMetadata;
          }
        }
      };
    },
    updateMetadata(newMetadata) {
      blockMetadata = {
        ...newMetadata,
        name: blockName,
        // Always preserve block name
        remoteModule
        // Always preserve remote module
      };
      if (frontierBlocks[blockName] && frontierBlocks[blockName].updateMetadata) {
        frontierBlocks[blockName].updateMetadata(newMetadata);
      }
    },
    initBlock() {
      RemoteComponent = createDynamicComponent();
    }
  };
  blockExport.register(frontierBlocks);
  blockExport.initBlock();
  if (frontierBlocks[blockName]) {
    frontierBlocks[blockName].initBlock = blockExport.initBlock;
  }
  return blockExport;
}
async function preloadFrontierMetadata() {
  const preloadPromises = Array.from(frontierBlockConfigs.entries()).map(async ([blockName, config]) => {
    try {
      const remoteModule = await remote(config.remoteModule);
      if (remoteModule == null ? void 0 : remoteModule.metadata) {
        const registryEntry = frontierBlocks[blockName];
        if (registryEntry && registryEntry.updateMetadata) {
          registryEntry.updateMetadata({
            ...remoteModule.metadata,
            name: blockName,
            remoteModule: config.remoteModule
          });
        }
      }
    } catch (error) {
      console.warn(`Failed to load metadata for ${blockName}:`, error);
    }
  });
  await Promise.all(preloadPromises);
}
export {
  createFrontierBlock,
  frontierBlockConfigs,
  preloadFrontierMetadata,
  setFrontierBlocksRegistry
};
